import {
  CollectDataSubjectRegions,
  type WorkflowConfig,
} from '@main/dsr-types';

import type { ActiveDataSubjectState } from '../ActiveDataSubject/slice';

/**
 * helper to check if a given workflow is valid for a data subject
 *
 * @param workflow - the workflow to verify that the activeDataSubject is valid for
 * @param activeDataSubject - the current active data subject
 * @returns true if the given active data subject is valid for the workflow
 */
export const isWorkflowAllowedForDataSubject = (
  workflow: Pick<
    WorkflowConfig,
    'subject' | 'regionList' | 'collectDataSubjectRegions'
  >,
  activeDataSubject: ActiveDataSubjectState['activeDataSubject'],
): boolean =>
  workflow.subject !== undefined &&
  activeDataSubject !== undefined &&
  workflow.subject.id === activeDataSubject.id;

/**
 * helper to get the allowed regions for a given list of workflows and
 * validates each workflow against a provided data subject
 *
 * @param workflows - the workflows to get all allowed regions from
 * @param activeDataSubject - the current active data subject
 * @returns the allowed regions for all workflows valid for the active data subject
 */
export const getAllowedRegions = (
  workflows: Pick<
    WorkflowConfig,
    'subject' | 'regionList' | 'collectDataSubjectRegions'
  >[],
  activeDataSubject: ActiveDataSubjectState['activeDataSubject'],
): {
  /** Any region that has been specified by a workflow */
  specifiedRegions: string[];
  /**
   * edge case - if there is a workflow for a data subject which is set to collect regions
   * but does not specify any specific regions we have to show all options regardless of
   * if there are other workflows that specify specific regions
   */
  hasWorkflowThatCollectsAnyRegion: boolean;
} => {
  const workflowsForActiveSubject = workflows.filter((wf) =>
    isWorkflowAllowedForDataSubject(wf, activeDataSubject),
  );
  const specifiedRegions = new Set<string>(
    workflowsForActiveSubject.flatMap((wf) => wf.regionList ?? []),
  );
  return {
    specifiedRegions: Array.from(specifiedRegions),
    hasWorkflowThatCollectsAnyRegion: workflowsForActiveSubject.some(
      (wf) =>
        wf.collectDataSubjectRegions === CollectDataSubjectRegions.Collect &&
        wf.regionList &&
        wf.regionList.length === 0,
    ),
  };
};
